import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Leaf, Mail, Lock, LogIn, Trophy, Award } from "lucide-react";
import GoogleAuthButton from "@/components/GoogleAuthButton";

export default function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const user = await login(email, password);
      const params = new URLSearchParams(location.search);
      const raw = params.get("returnTo") || location.state?.from?.pathname;
      const roleDefault = user.role === "admin" ? "/admin" : "/dashboard";
      // Do not send admin into onboarding routes and vice-versa.
      const isReferrerRoute = raw && (raw === "/dashboard" || raw.startsWith("/vacancies") || raw.startsWith("/recommend") || raw.startsWith("/notifications") || raw.startsWith("/onboarding") || raw.startsWith("/v/"));
      const isAdminRoute = raw && raw.startsWith("/admin");
      let target = roleDefault;
      if (raw) {
        if (user.role === "admin" && isReferrerRoute) target = "/admin";
        else if (user.role !== "admin" && isAdminRoute) target = "/dashboard";
        else target = raw;
      }
      navigate(target, { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--bone)] grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between p-14 bg-[var(--forest)] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10 paper-noise" aria-hidden />
        <Link to="/" className="flex items-center gap-3 relative z-10">
          <div className="w-11 h-11 rounded-2xl bg-white/10 border border-white/20 grid place-items-center"><Leaf className="w-5 h-5"/></div>
          <div>
            <div className="font-heading text-xl">CARING</div>
            <div className="eyebrow text-white/70">Referências</div>
          </div>
        </Link>
        <div className="relative z-10">
          <div className="eyebrow text-white/70">Bem-vindo de volta</div>
          <h2 className="font-heading text-5xl mt-3 leading-tight">Cada recomendação<br/>é um cuidado.</h2>
          <p className="text-white/80 mt-6 max-w-md">Continue a construir a rede de confiança da CARING. Acompanhe candidatos, prémios e pagamentos.</p>

          <div className="mt-8 space-y-3 max-w-md">
            <div className="rounded-2xl bg-white/10 border border-white/15 p-4 backdrop-blur-sm" data-testid="login-bonus-hint">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-xl bg-[#E7B24A]/25 grid place-items-center text-[#F3D07A]">
                  <Trophy className="w-4 h-4"/>
                </div>
                <div>
                  <div className="text-white font-heading text-lg leading-tight">Prémios extra por emblemas</div>
                  <p className="text-white/70 text-xs mt-1">Além dos 15&nbsp;€ por cada contratação, ganha bónus adicionais ao atingir emblemas <strong className="text-[#F3D07A]">Bronze</strong>, <strong className="text-white">Prata</strong> e <strong className="text-[#F3D07A]">Ouro</strong>.</p>
                </div>
              </div>
            </div>
            <div className="rounded-2xl bg-white/5 border border-white/10 p-4 backdrop-blur-sm">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-xl bg-white/10 grid place-items-center text-white/80">
                  <Award className="w-4 h-4"/>
                </div>
                <div>
                  <div className="text-white font-heading text-lg leading-tight">Subindo o ranking</div>
                  <p className="text-white/70 text-xs mt-1">Veja a sua posição no top de referenciadores no painel principal e desbloqueie novos escalões automaticamente.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="relative z-10 text-sm text-white/60">© {new Date().getFullYear()} CARING</div>
      </div>

      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="eyebrow">Entrar</div>
          <h1 className="font-heading text-4xl mt-2 text-[var(--ink)]">Aceda à sua conta</h1>
          <p className="text-[var(--ink-soft)] mt-2">Não tem conta? <Link to="/register" className="text-[var(--forest)] underline" data-testid="link-to-register">Registe-se</Link></p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <label className="text-sm font-medium text-[var(--ink)]">Email</label>
              <div className="mt-1.5 relative">
                <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ink-soft)]"/>
                <input
                  type="email"
                  data-testid="login-email-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="voce@exemplo.pt"
                  className="w-full pl-10 pr-3 py-3 rounded-xl bg-[var(--muted)] border border-[var(--border)] focus:outline-none focus:ring-2 focus:ring-[var(--forest)] transition-shadow"
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-[var(--ink)]">Palavra-passe</label>
              <div className="mt-1.5 relative">
                <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ink-soft)]"/>
                <input
                  type="password"
                  data-testid="login-password-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="••••••••"
                  className="w-full pl-10 pr-3 py-3 rounded-xl bg-[var(--muted)] border border-[var(--border)] focus:outline-none focus:ring-2 focus:ring-[var(--forest)]"
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={busy}
              className="pill-btn pill-primary w-full justify-center !py-3.5"
              data-testid="login-submit-btn"
            >
              <LogIn className="w-4 h-4"/> {busy ? "A entrar…" : "Entrar"}
            </button>
            <Link to="/forgot-password" className="block text-center text-sm text-[var(--ink-soft)] underline mt-2" data-testid="link-forgot-password">Esqueceu-se da palavra-passe?</Link>
          </form>

          <div className="my-6 flex items-center gap-3 text-xs text-[var(--ink-soft)]">
            <div className="flex-1 h-px bg-[var(--border)]" />
            <div>ou continue com</div>
            <div className="flex-1 h-px bg-[var(--border)]" />
          </div>

          <GoogleAuthButton label="Entrar com Google" testId="login-google-btn" />
        </div>
      </div>
    </div>
  );
}
